// eslint-disable-next-line @eslint-community/eslint-comments/disable-enable-pair
/* eslint-disable camelcase */
import { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames/bind';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper/types';
import { Button } from '@/_kit/button';
import { EButtonColor, EButtonSize } from '@/_kit/button/_types';
import { ArrowRightIcon } from '@/_kit/icons/arrow-right-icon';
import { ArrowLeftIcon } from '@/_kit/icons/arrow-left-icon';
import { EAnalytics } from '@/_constants/analytics';
import { useAnalytics } from '@/_hooks/use-analytics';
import {
  EBlockName,
  EButtonName,
  EVariant,
  TSlide,
} from '@/_components/promo-zone/_types';
import { SegmentedProgressBar } from './_components/segmented-progress-bar';
import 'swiper/css';
import 'swiper/css/navigation';
import { Slide } from './_components/slide';
import { DURATION_MS } from './_components/_constants';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Slider';

type TProps = {
  variant: EVariant;
  slides: Array<TSlide>;
  sliderId: number;
  activeSlideIndex: number;
  setActiveSlideIndex: (index: number) => void;
  blockName: EBlockName;
};

export const Slider = ({
  variant,
  slides,
  sliderId,
  activeSlideIndex,
  setActiveSlideIndex,
  blockName,
}: TProps) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const sendEvent = useAnalytics();
  const sliderObserverRef = useRef<HTMLDivElement | null>(null);
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);

  const currentSlideId = slides[activeSlideIndex]?.id;
  const sliderObserverRefCurrent = sliderObserverRef.current;

  useEffect(() => {
    if (sliderObserverRefCurrent) {
      const observer = new IntersectionObserver(([entry]) => {
        setIsIntersecting(Boolean(entry?.isIntersecting));
      });

      observer.observe(sliderObserverRefCurrent);

      return () => observer.unobserve(sliderObserverRefCurrent);
    }
  }, [sliderObserverRefCurrent]);

  const handleAnalyticsEvent = useCallback(
    (buttonName: EButtonName, slideTitle: string | undefined) => {
      sendEvent({
        name: EAnalytics.SUPPLIER_LANDING_PROMO_T,
        params: {
          name_block: blockName,
          button_name: buttonName,
          name_subblock: slideTitle || '',
        },
      });
    },
    [blockName, sendEvent],
  );

  const handleArrowButtonsClick = useCallback(() => {
    handleAnalyticsEvent(EButtonName.SWIPE, currentSlideId);
  }, [currentSlideId, handleAnalyticsEvent]);

  const updateActiveSlideIndex = useCallback(
    (swiper: SwiperType) => {
      setActiveSlideIndex(swiper.realIndex);
    },
    [setActiveSlideIndex],
  );

  const handleSegmentClick = useCallback(
    (index: number) => {
      if (swiperRef.current) {
        handleAnalyticsEvent(EButtonName.SWIPE, currentSlideId);
        swiperRef.current.slideToLoop(index);
      }
    },
    [currentSlideId, handleAnalyticsEvent],
  );

  const handleSwiper = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper;
  }, []);

  return (
    <div className={cn(BLOCK_NAME, `${BLOCK_NAME}--${variant}`)}>
      <div className={cn(`${BLOCK_NAME}__progress-bar-wrapper`)}>
        <SegmentedProgressBar
          activeIndex={activeSlideIndex}
          onSegmentClick={handleSegmentClick}
          totalSlides={slides.length}
        />
      </div>
      <div className={cn(`${BLOCK_NAME}__buttons`)}>
        <div
          className={cn(
            `${BLOCK_NAME}__button`,
            `${BLOCK_NAME}__button--prev`,
            `${BLOCK_NAME}__button--prev-${sliderId}`,
          )}
        >
          <Button
            backgroundColor={EButtonColor.EMPTY}
            onClick={handleArrowButtonsClick}
            rightIcon={ArrowLeftIcon}
            size={EButtonSize.MEDIUM}
            strokeColor={EButtonColor.WHITE}
          />
        </div>
        <div
          className={cn(
            `${BLOCK_NAME}__button`,
            `${BLOCK_NAME}__button--next`,
            `${BLOCK_NAME}__button--next-${sliderId}`,
          )}
        >
          <Button
            backgroundColor={EButtonColor.EMPTY}
            onClick={handleArrowButtonsClick}
            rightIcon={ArrowRightIcon}
            size={EButtonSize.MEDIUM}
            strokeColor={EButtonColor.WHITE}
          />
        </div>
      </div>
      <Swiper
        autoplay={{
          delay: DURATION_MS,
          disableOnInteraction: false,
        }}
        className={cn(`${BLOCK_NAME}__swiper`)}
        loop
        modules={[Autoplay, Navigation]}
        navigation={{
          nextEl: `.${BLOCK_NAME}__button--next-${sliderId}`,
          prevEl: `.${BLOCK_NAME}__button--prev-${sliderId}`,
        }}
        onInit={updateActiveSlideIndex}
        onSlideChange={updateActiveSlideIndex}
        onSwiper={handleSwiper}
        pagination={{
          clickable: true,
          type: 'progressbar',
        }}
        slidesPerView={1}
        spaceBetween={0}
      >
        {slides.map((slideProps, index) => (
          <SwiperSlide key={index}>
            <Slide
              {...slideProps}
              blockName={blockName}
              isIntersectingSlider={isIntersecting}
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <div ref={sliderObserverRef} className={cn(`${BLOCK_NAME}__observer`)} />
    </div>
  );
};
